import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { GetTermDetailResponse } from '@/api/generated/schemas';
import { SerieLayer } from '@/api/generated/schemas';
import { useGetVenuaPlannerTermDetail } from '@/api/generated/venua-planner-planning/venua-planner-planning';
import { DETAIL_QUERY_STALE_TIME_MS } from '@/shared/lib/queryClient';
import { cn } from '@/shared/lib/utils';
import {
  Alert,
  Badge,
  FormActionBar,
  Panel,
  Tabs,
  WindowContent,
  toast,
  type TabItem,
} from '@/shared/ui';
import { useAgendaParams } from '@/shared/navigation/react/useAgendaParams';
import { useAgendaState } from '@/shared/navigation/react/useAgendaState';
import { useNavigator } from '@/shared/navigation/react/useNavigator';
import { useWindowTitle, WindowHeaderExtras } from '@/shared/windowing';
import { ProductLink } from '../../product';
import { ApproveLayerButton } from '../internal/ApproveLayerButton';
import { invalidateTermScopes, useChangeTermLayerState } from '../internal/useChangeLayerState';
import { SerieLayerPipeline } from '../internal/SerieLayerPipeline';
import { SerieLink } from '../internal/SerieLink';
import { SerieLockedPane } from '../internal/SerieLockedPane';
import { CostSchemeTab } from '../internal/costScheme';
import { CalculationTab } from '../internal/calculation';
import { SERIE_TAB_KEYS, toSerieStateKey, type SerieTabKey } from '../internal/serieLayers';
import { termDetailAgenda } from './termDetailAgenda';
import { TermBasicsForm } from './components/TermBasicsForm';
import { useTermDetailForm } from './useTermDetailForm';
import {
  TransportSchemeTab,
  useSetTermTransportSchemeOwnership,
  useTransportSchemeEditor,
} from '../internal/transportScheme';

const TERM_RANGE_FORMAT = new Intl.DateTimeFormat('cs-CZ', {
  day: 'numeric',
  month: 'numeric',
  year: 'numeric',
});

/** Rozsah termínu „1. 11. 2026 → 8. 11. 2026“ (read-only v hlavičce). */
function formatTermRange(from: string, to: string | null): string {
  const fromLabel = TERM_RANGE_FORMAT.format(new Date(from));
  if (!to) return fromLabel;
  return `${fromLabel} → ${TERM_RANGE_FORMAT.format(new Date(to))}`;
}

/** Tečkový oddělovač položek v hlavičce. */
function HeaderDot() {
  return <span className="text-phx-text-faint">·</span>;
}

interface TermDetailPageProps {
  layerId: string;
}

/**
 * Thin loader: vyřeší parametry agendy a načte detail termínu (s krátkým `staleTime`, aby detail nikdy needitoval
 * zastaralá data). Vlastní obrazovku vykreslí až s načteným detailem, aby `useForm` dostal správné výchozí hodnoty
 * hned (žádný `form.reset`).
 */
export function TermDetailPage({ layerId }: TermDetailPageProps) {
  const { t } = useTranslation('venua-planner');
  const { open } = useAgendaParams(termDetailAgenda, layerId);
  const { productId, serieId, termId } = open;

  const detailQuery = useGetVenuaPlannerTermDetail(productId, serieId, termId, {
    query: { staleTime: DETAIL_QUERY_STALE_TIME_MS },
  });

  if (detailQuery.isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-phx-text-muted text-phx-sm">{t('termDetail.loading')}</p>
      </div>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <div className="p-phx-5 h-full">
        <Alert severity="error" title={t('termDetail.loadErrorTitle')}>
          {t('termDetail.loadErrorDescription')}
        </Alert>
      </div>
    );
  }

  return (
    <TermDetailView
      layerId={layerId}
      productId={productId}
      serieId={serieId}
      termId={termId}
      detail={detailQuery.data}
    />
  );
}

interface TermDetailViewProps {
  layerId: string;
  productId: string;
  serieId: string;
  termId: string;
  detail: GetTermDetailResponse;
}

/**
 * Detail jednoho termínu. Základní údaje se editují přes `UpdateTerm` (editovatelný jen kód), doprava přes
 * `UpdateTermMainTransportScheme` s dědičností ze série. Vrstvy se schvalují per-termín („Uložit & schválit“).
 */
function TermDetailView({ layerId, productId, serieId, termId, detail }: TermDetailViewProps) {
  const { t } = useTranslation('venua-planner');
  const navigator = useNavigator();
  const queryClient = useQueryClient();

  // Záložka žije v URL stavu vrstvy (replace, ne push) — reload i deep-link ze sidebaru kalendáře
  // zůstanou na stejné vrstvě dat a výběr dalšího detailu ve stromu ji převezme.
  const { state } = useAgendaParams(termDetailAgenda, layerId);
  const agendaState = useAgendaState(termDetailAgenda, layerId);
  const activeTab: SerieTabKey = state.tab ?? 'basics';
  const setActiveTab = useCallback((tab: SerieTabKey) => agendaState.patch({ tab }), [agendaState]);

  // Rozbalený termín na Yield Managementu — taky v URL (stejná komponenta jako u seriálu).
  const expandedTermId = state.calcTermId ?? null;
  const setExpandedTermId = useCallback(
    (id: string | null) =>
      id ? agendaState.patch({ calcTermId: id }) : agendaState.remove('calcTermId'),
    [agendaState]
  );

  const closeDetail = useCallback(() => {
    void navigator.closeLayer(layerId);
  }, [navigator, layerId]);

  // Dynamický název okna (taskbar / historie / hlavička) = kód termínu.
  useWindowTitle(detail.code);

  /** Invalidace detailu termínu + navazujících seznamů (seriál, strom, kalendář), aby se stavy propsaly všude. */
  const invalidate = useCallback(
    () => invalidateTermScopes(queryClient, { productId, serieId, termId }),
    [queryClient, productId, serieId, termId]
  );

  const { form, submit, isSubmitting } = useTermDetailForm({
    productId,
    serieId,
    termId,
    detail,
    onSaved: () => {
      invalidate();
      toast({ severity: 'success', description: t('termDetail.toast.saved') });
    },
    onError: () => {
      toast({
        severity: 'error',
        title: t('termDetail.toast.errorTitle'),
        description: t('termDetail.toast.saveError'),
      });
    },
  });

  // Termín se sdílenou definicí vidí plné panely read-only — plánovač tak ví, čím termín skutečně letí,
  // a nemusí přepínat na seriál. Vlastní hodnoty (linka, ruční cena, kapacita) uložit může.
  const transportEditor = useTransportSchemeEditor({
    productId,
    serieId,
    termId,
    scope: 'term',
    enabled: activeTab === 'operation',
  });

  const setOwnScheme = useSetTermTransportSchemeOwnership({
    productId,
    serieId,
    termId,
    onDone: invalidate,
  });

  // Změna stavu vrstev termínu (Připravit / Schválit / Vrátit / Vrátit schválení) přes sjednocený endpoint –
  // toasty i invalidace řeší hook. Nabídku „schválit až po X“ odvodí `ApproveLayerButton` z vrstev série.
  const { changeState, isPending: changing } = useChangeTermLayerState({
    productId,
    serieId,
    termId,
  });

  const operationLayer = detail.layers.find(
    (entry) => entry.sortOrder === SERIE_TAB_KEYS.indexOf('operation')
  );
  const operationStateKey = operationLayer ? toSerieStateKey(operationLayer.state) : 'locked';
  const operationLocked = operationStateKey === 'locked';
  // Doprava termínu je read-only, jakmile je vrstva připravená / schválená; editovat lze jen v Draftu.
  const operationReadOnly = operationStateKey === 'prepared' || operationStateKey === 'approved';
  const operationEditable = activeTab === 'operation' && operationStateKey === 'draft';
  // Spodní akční lišta ukládá formulář aktivní záložky: na editovatelné dopravě (bus) její schéma, jinak
  // základní údaje. Na neaktivních variantách operation tabu (locked / schváleno / non-bus) se ukládání zakáže.
  const activeSubmit = operationEditable ? transportEditor.submit : submit;
  const activeSaving = operationEditable ? transportEditor.isSubmitting : isSubmitting;
  const saveDisabled = activeTab === 'operation' && !operationEditable;

  const tabItems: TabItem<SerieTabKey>[] = SERIE_TAB_KEYS.map((tabKey, index) => {
    const layer = detail.layers.find((entry) => entry.sortOrder === index);
    const locked = layer ? toSerieStateKey(layer.state) === 'locked' : false;
    return {
      value: tabKey,
      label: (
        <span className={cn('gap-phx-2 inline-flex items-center', locked && 'opacity-60')}>
          {t(`termDetail.tabs.${tabKey}`)}
          {locked ? <Lock size={11} className="text-phx-text-faint" /> : null}
        </span>
      ),
    };
  });

  // Schvalovací tlačítko aktivní vrstvy. Nabídku „schválit až po X“ si `ApproveLayerButton` odvodí
  // z vrstev termínu a seriálu; disabled stav plyne ze stavu vrstvy, tady se přidávají jen důvody
  // navíc (rozepsaný formulář).
  const approveAction: React.ReactNode =
    activeTab === 'basics' ? (
      <ApproveLayerButton
        currentLayer={SerieLayer.Terms}
        layers={detail.layers}
        serieLayers={detail.serieLayers}
        onBeforeAction={submit}
        onChangeState={changeState}
        disabled={isSubmitting}
        loading={changing}
      />
    ) : activeTab === 'operation' && !operationLocked ? (
      <ApproveLayerButton
        currentLayer={SerieLayer.OperationScheme}
        layers={detail.layers}
        serieLayers={detail.serieLayers}
        onBeforeAction={operationEditable ? transportEditor.submit : undefined}
        onChangeState={changeState}
        disabled={transportEditor.isSubmitting}
        loading={changing}
      />
    ) : undefined;

  // Product cost scheme (Cost Scheme agenda) — full-bleed board that takes over the whole content area and
  // renders its own footer, so it must not sit inside the centered scrolling column + FormActionBar.
  const productCostLayer = detail.layers.find(
    (entry) => entry.sortOrder === SERIE_TAB_KEYS.indexOf('productCost')
  );
  const productCostLocked = productCostLayer
    ? toSerieStateKey(productCostLayer.state) === 'locked'
    : true;
  const showCostBoard = activeTab === 'productCost' && !productCostLocked;

  const pageHeader = (
    <div className="flex flex-col">
      {/* Podtitulek okna (portál do hlavičky) = produkt + libovolný obsah (badge zrušení). */}
      <WindowHeaderExtras subtitle={`${detail.productCode} · ${detail.productName}`}>
        {detail.isCancelled ? (
          <Badge tone="warning" appearance="soft">
            {t('termDetail.basics.cancelled')}
          </Badge>
        ) : null}
      </WindowHeaderExtras>
      <div className="text-phx-sm gap-phx-2 flex flex-wrap items-center">
        <span className="text-phx-text font-semibold">
          {formatTermRange(detail.dateFrom, detail.dateTo)}
        </span>
        {detail.nights != null ? (
          <>
            <HeaderDot />
            <span className="text-phx-text font-semibold">
              {t('termDetail.basics.nights', { count: detail.nights })}
            </span>
          </>
        ) : null}
        <HeaderDot />
        <span className="text-phx-text-muted">{detail.code}</span>
        <HeaderDot />
        <SerieLink productId={productId} serieId={serieId}>
          <span className="text-phx-text-muted">{detail.serieName}</span>
        </SerieLink>
        <HeaderDot />
        <ProductLink productId={productId}>
          <span className="text-phx-text-muted">
            <span className="font-mono">{detail.productCode}</span> · {detail.productName}
          </span>
        </ProductLink>
        {detail.isCancelled ? (
          <>
            <HeaderDot />
            <Badge tone="warning" appearance="soft">
              {t('termDetail.basics.cancelled')}
            </Badge>
          </>
        ) : null}
      </div>
      <SerieLayerPipeline layers={detail.layers} />
    </div>
  );
  const tabsEl = (
    <Tabs<SerieTabKey>
      variant="underline"
      value={activeTab}
      onChange={setActiveTab}
      items={tabItems}
    />
  );

  if (showCostBoard) {
    return (
      <div className="flex h-full min-h-0 flex-col" data-testid="term-detail">
        <div className="px-phx-5 pt-phx-5 shrink-0">
          <div className="gap-phx-3 flex w-full flex-col">
            {pageHeader}
            {tabsEl}
          </div>
        </div>
        <div className="min-h-0 flex-1">
          <CostSchemeTab
            scope="term"
            productId={productId}
            serieId={serieId}
            termId={termId}
            layers={detail.layers}
            serieLayers={detail.serieLayers}
            onBack={closeDetail}
          />
        </div>
      </div>
    );
  }

  // Calculation — full-bleed board s vlastní patičkou, stejně jako Cost Scheme.
  const calculationLayer = detail.layers.find(
    (entry) => entry.sortOrder === SERIE_TAB_KEYS.indexOf('calculation')
  );
  const calculationLocked = calculationLayer
    ? toSerieStateKey(calculationLayer.state) === 'locked'
    : true;
  const showCalculationBoard = activeTab === 'calculation' && !calculationLocked;

  if (showCalculationBoard) {
    return (
      <div className="flex h-full min-h-0 flex-col" data-testid="term-detail">
        <div className="px-phx-5 pt-phx-5 shrink-0">
          <div className="gap-phx-3 flex w-full flex-col">
            {pageHeader}
            {tabsEl}
          </div>
        </div>
        <div className="min-h-0 flex-1">
          <CalculationTab
            scope="term"
            productId={productId}
            serieId={serieId}
            termId={termId}
            layers={detail.layers}
            serieLayers={detail.serieLayers}
            onBack={closeDetail}
            expandedTermId={expandedTermId}
            onExpandedTermIdChange={setExpandedTermId}
          />
        </div>
      </div>
    );
  }

  return (
    <WindowContent
      as="div"
      data-testid="term-detail"
      footer={
        <FormActionBar
          onBack={closeDetail}
          onSave={activeSubmit}
          saving={activeSaving}
          disabled={saveDisabled}
          extraActions={approveAction}
        />
      }
    >
      {pageHeader}

      {tabsEl}

      {activeTab === 'basics' ? (
        <form
          className="gap-phx-3 flex flex-col"
          onSubmit={(event) => {
            event.preventDefault();
            void form.handleSubmit();
          }}
        >
          <Panel variant="filled" title={t('termDetail.basics.panelTitle')}>
            <TermBasicsForm form={form} detail={detail} productId={productId} serieId={serieId} />
          </Panel>
        </form>
      ) : activeTab === 'operation' ? (
        operationLocked ? (
          <SerieLockedPane title={t('termDetail.tabs.operation')} />
        ) : (
          <TransportSchemeTab
            editor={transportEditor}
            productId={productId}
            serieId={serieId}
            termId={termId}
            scope="term"
            readOnly={operationReadOnly}
            onOpenTerm={() => undefined}
            onToggleOwnScheme={setOwnScheme}
          />
        )
      ) : (
        <SerieLockedPane title={t(`termDetail.tabs.${activeTab}`)} />
      )}
    </WindowContent>
  );
}
